import React, { useState, useEffect, useMemo, useRef } from "react";
import Submission from "../../../../interfaces/Submission";
import FirebaseService from "../../../../services/firebase/FirebaseService";
import Table from "../Table/Table";
import isEqual from "react-fast-compare";
import "./AssignmentView.css";
import { useHistory } from "react-router";
import {
  formatLastLogin,
  // sortDates,
  sortStrings,
  sortNumbers,
} from "../Table/TableSortAndFormatFunctions";
import { accuracyToColor, accuracyToBand } from "../../../../utils/AccuracyToBand";
import { MenuItem, Select, FormControl, InputLabel } from "@material-ui/core";
import FilterIcon from "../../../../assets/icons/Filter_By_Class_Icon_orange.svg";

import EmptyState from "../../../EmptyState";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state/configureStore";
import ReportHeader from "../ReportHeader";
import { findClassroom } from "../TeacherHomeScreen/FilteringFromURLFunctions";
import Classroom from "../../../../interfaces/Classroom";

const findStudentClassroom = (classrooms: Array<Classroom>, student: string) =>
  classrooms.find(classroom => !!classroom.students[student])?.id;

// useMemo with deep equality comparison function, so page only updates when props change
const AssignmentView = React.memo(() => {
  // pull assignment and class
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const [selectedClassId, setSelectedClassId] = useState<string | undefined>(
    new URLSearchParams(window.location.search).get("classId") || undefined,
  );

  // students in selected classroom(s)
  const [selectedStudents, setSelectedStudents] = useState<{ [key: string]: boolean }>({});

  // Get teacher data from store
  const allClassrooms = useSelector((state: RootState) => state.teacherState.classrooms);
  const assignmentClassrooms = useMemo(
    () => allClassrooms.filter(classroom => classroom.assignments?.includes(assignmentId)),
    [assignmentId, allClassrooms],
  );

  const students = useSelector((state: RootState) => state.teacherState.students);
  const assignments = useSelector((state: RootState) => state.teacherState.assignments);

  const assignment = assignments[assignmentId] ?? undefined;

  const history = useHistory();

  // Student info needed for table, key is student id
  const [studentSubmissions, setStudentSubmissions] = useState({} as Record<string, Submission>);

  // Keep track of student watches
  // useRef to keep value across renders
  const studentWatches = useRef({} as Record<string, () => void>);

  useEffect(() => {
    const getAllStudents = () => {
      const temp: { [key: string]: boolean } = {};
      assignmentClassrooms.forEach(classroom => {
        for (const student in classroom.students) {
          temp[student] = classroom.students[student] ?? false;
        }
      });
      return temp;
    };
    if (assignment) {
      // Get local copy of students to be displayed
      const localStudents: { [key: string]: boolean } = !selectedClassId
        ? getAllStudents()
        : findClassroom(assignmentClassrooms, selectedClassId)?.students ?? {};

      setSelectedStudents(localStudents);

      // For student, watch classroomReadingStats and add to state array (StudentInfo)
      const watchStudentInfo = async (studentId: string) => {
        // Watch classroomReadingStats for student
        studentWatches.current[studentId] = FirebaseService.watchSubmission(
          studentId,
          assignment.assignmentDetails.bookId,
          async studentSubmission => {
            if (studentSubmission) {
              setStudentSubmissions(state => ({
                ...state,
                [studentId]: studentSubmission,
              }));
            } else {
              const noSubmission: Submission = {
                accuracy: 0,
                classId: selectedClassId || findStudentClassroom(allClassrooms, studentId) || "",
                pagesRead: 0,
                readingStatus: "incomplete",
                totalTime: 0,
                totalWordsRead: 0,
                userId: studentId,
                wordsPerMin: 0,
                turnedIn: false,
              };
              setStudentSubmissions(state => ({
                ...state,
                [studentId]: noSubmission,
              }));
            }
          },
        );
      };

      // Create watch for each student
      for (const student in localStudents) {
        const studentObj = localStudents[student];
        if (studentObj && !studentWatches.current?.hasOwnProperty(student)) {
          watchStudentInfo(student);
        }
      }
    }

    // Cleanup useEffect before rendering new students or new classroom
    const currentWatches = studentWatches.current;

    return function cleanup() {
      // cleanup watch for each student in studentWatches
      for (const student in currentWatches) {
        currentWatches[student]?.();
        delete currentWatches[student];
      }
    };
    // Run use effect when classroom, selected class, or assignment changes
  }, [selectedClassId, assignmentClassrooms, allClassrooms, assignment]);

  // Change class function on dropdown menu selection
  const changeClass = (classId: string) => {
    setSelectedClassId(classId);

    if (!classId || classId === "all") history.push(`/Assignments/${assignmentId}`);
    else history.push(`/Assignments/${assignmentId}?classId=${classId}`);
  };

  // Direct to student report view
  // "/Assignment/:assignmentId/Class/:classId/Student/:studentId"
  const createStudentLinkURL = (row: any) =>
    `/Assignment/${assignmentId}/Class/${row.original.classId}/Student/${row.original.userId}`;

  // Columns for table
  const columns = useMemo(
    () => [
      {
        // student column
        Header: "Students",
        accessor: (data: any): string => {
          const firstName = students[data.userId]?.firstName;
          const lastName = students[data.userId]?.lastName;
          const displayName = students[data.userId]?.displayName;
          if (firstName && lastName) {
            return `${lastName}, ${firstName}`;
          } else if (displayName) {
            return displayName;
          }
          return "Student";
        },
        sortType: sortStrings,
      },
      {
        // completion % column
        Header: "Completion",
        accessor: (data: any) => data.completionPercentage,
        sortType: sortNumbers,
        Cell: ({ value }: any) => (isNaN(value) ? "N/A" : Math.trunc(value * 100).toString() + "%"),
      },
      {
        // wpm column
        Header: "WPM",
        accessor: (data: any) => data.wordsPerMin,
        sortType: sortNumbers,
        Cell: (props: any) => (isNaN(props.value) ? "N/A" : Math.trunc(props.value)),
      },
      {
        // accuracy column
        Header: "Accuracy",
        accessor: (data: any) => data.accuracy,
        sortType: sortNumbers,
        // cell is what displays in the table, so put svg circle here
        // use accuracyToColor to get the appropriate color
        Cell: (props: any) =>
          props.value || props.value === 0 ? (
            <div className="accuracy-container">
              <svg height="16" width="16">
                <circle cx="8" cy="8" r="8" fill={accuracyToColor(props.value)} />
              </svg>
              <div className="accuracy-popup">
                {accuracyToBand(props.value)}
                <div className="caret"></div>
              </div>
            </div>
          ) : (
            ""
          ),
      },
      {
        // reading time column
        Header: "Minutes",
        accessor: (data: any) => data.totalTime,
        sortType: sortNumbers,
        // cell is what displays in the table, so use formatted time read
        Cell: (props: any) => {
          // if student data doesn't exist, return empty string
          // else return formatted time (include check for zero)
          if (isNaN(props.value)) {
            return "N/A";
          }
          const mins = Math.trunc(props.value / 60);
          const seconds = Math.trunc(props.value % 60);
          return `${mins}:${seconds < 10 ? "0" + seconds.toString() : seconds}`;
        },
      },
      {
        // total words column
        Header: "Words",
        accessor: (data: any) => data.totalWordsRead,
        sortType: sortNumbers,
        Cell: (props: any) => props.value ?? "N/A",
      },
      {
        // last login column
        Header: "Turned In",
        // If assignment turned in, return time of submission
        accessor: (data: any) => (data.turnedIn ? data.timeSubmitted : undefined),
        // sortType: sortDates,
        Cell: formatLastLogin,
      },
    ],
    [students],
  );

  // check if assignment exists
  if (!assignment) {
    return <p>Assignment does not exist</p>;
  }

  return (
    <div className="AssignmentView">
      <ReportHeader />
      <FormControl variant="outlined">
        <InputLabel>
          <img className="FilterIcon" src={FilterIcon} aria-hidden alt="" />
          Filter By Class
        </InputLabel>
        <Select
          label="xxlFilter By Class"
          onChange={val => changeClass(val.target.value as string)}
          value={selectedClassId}
          disableUnderline={true}
        >
          {[<MenuItem value={undefined}>{"All Classes"}</MenuItem>].concat(
            assignmentClassrooms.map(classroom => <MenuItem value={classroom.id}>{classroom.className}</MenuItem>),
          )}
        </Select>
      </FormControl>
      {!Object.values(studentSubmissions).length ? (
        <EmptyState isClassPage>No Students Found</EmptyState>
      ) : (
        /* object.values is not compatible with ie */
        <Table
          kind="student"
          columns={columns}
          data={Object.entries(studentSubmissions)
            .filter(([studentKey, _]) => selectedStudents.hasOwnProperty(studentKey))
            .map(([_, v]) => v)}
          createRowLinkURL={createStudentLinkURL}
        />
      )}
    </div>
  );
}, isEqual);

export default AssignmentView;
